import React, { useState, useEffect, useMemo } from "react";
import "./ArteComponent.css";
import ImageWithLoader from "../ImageWithLoader";

// Lightbox moderno
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";

const API_BASE = "https://api.fallaplazarodrigo.es:999";

const buildUrl = (src) => {
  if (!src) return "";
  return src.startsWith("http") ? src : `${API_BASE}${src}`;
};

export const ArteComponent = (props) => {
  const [page, setPage] = useState(1);
  const itemsPerPage = 8;
  const totalPages = Math.ceil(props.Data.length / itemsPerPage);
  const [yearRanges, setYearRanges] = useState([]);

  // Estado para el lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Slides globales (todas las fallas, grande + infantil)
  const slides = useMemo(() => {
    return props.Data.flatMap((item) => {
      const arr = [];

      if (item.Grande) {
        arr.push({
          src: buildUrl(item.Grande.image),
          thumb: buildUrl(item.Grande.imageMin),
          description: `${item.year} · ${props.I18n.Artist}: ${item.Grande.artista} · ${props.I18n.Motto}: ${item.Grande.lema}`,
        });
      }

      if (item.Infantil) {
        arr.push({
          src: buildUrl(item.Infantil.image),
          thumb: buildUrl(item.Infantil.imageMin),
          description: `${item.year} · Infantil · ${props.I18n.Artist}: ${item.Infantil.artista} · ${props.I18n.Motto}: ${item.Infantil.lema}`,
        });
      }

      return arr;
    });
  }, [props.Data, props.I18n]);

  useEffect(() => {
    const ranges = [];
    for (let i = 1; i <= totalPages; i++) {
      const startIndex = (i - 1) * itemsPerPage;
      const endIndex = Math.min(
        startIndex + itemsPerPage,
        props.Data.length
      );
      const startYear = props.Data[startIndex]?.year;
      const endYear = props.Data[endIndex - 1]?.year;
      if (startYear && endYear) {
        ranges.push(`${startYear} - ${endYear}`);
      } else {
        ranges.push("");
      }
    }
    setYearRanges(ranges);
  }, [props.Data, totalPages]);

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  // Calcula el índice global del slide (usamos 2 por año: grande e infantil)
  const getSlideIndex = (yearIndexGlobal, tipo) => {
    // tipo: 'grande' -> offset 0, 'infantil' -> offset 1
    const base = yearIndexGlobal * 2;
    return tipo === "infantil" ? base + 1 : base;
  };

  if (!props.Data.length) return null;

  return (
    <>
      <div>
        <h2 className="titulo-page">
          {props.I18n.SketchesOfOurArtists}
        </h2>

        <div className="paginado">
          {pageNumbers.map((pageNumber, index) => (
            <button
              key={pageNumber}
              onClick={() => handlePageChange(pageNumber)}
              className={pageNumber === page ? "selected" : ""}
            >
              ({yearRanges[index] || ""})
            </button>
          ))}
        </div>

        <span className="miguel">
          Historico de Miguel Ángel Argente, bibliotecario archiver
        </span>

        <div className="arte-content">
          {props.Data.slice(
            (page - 1) * itemsPerPage,
            page * itemsPerPage
          ).map((item, indexInPage) => {
            const globalYearIndex =
              (page - 1) * itemsPerPage + indexInPage;

            const grandeSlideIndex = getSlideIndex(
              globalYearIndex,
              "grande"
            );
            const infantilSlideIndex = getSlideIndex(
              globalYearIndex,
              "infantil"
            );

            const grandeThumb = buildUrl(item.Grande?.imageMin);
            const infantilThumb = buildUrl(item.Infantil?.imageMin);

            return (
              <div key={globalYearIndex}>
                <hr />
                <h2>{item.year}</h2>
                <hr />

                {/* Falla Grande */}
                {item.Grande && (
                  <div className="card-container">
                    <div className="card">
                      <button
                        type="button"
                        className="img-content"
                        onClick={() => {
                          setLightboxIndex(grandeSlideIndex);
                          setLightboxOpen(true);
                        }}
                      >
                        <ImageWithLoader
                          src={grandeThumb}
                          alt={`Falla Grande del ${item.year}`}
                        />
                      </button>
                      <div className="content">
                        <p className="heading">{item.year}</p>
                        <p>
                          <strong>{props.I18n.Artist}:</strong>{" "}
                          {item.Grande.artista}
                          <br />
                          <strong>{props.I18n.Motto}:</strong>{" "}
                          {item.Grande.lema}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Falla Infantil */}
                {item.Infantil && (
                  <div className="card-container">
                    <div className="card">
                      <button
                        type="button"
                        className="img-content"
                        onClick={() => {
                          setLightboxIndex(infantilSlideIndex);
                          setLightboxOpen(true);
                        }}
                      >
                        <ImageWithLoader
                          src={infantilThumb}
                          alt={`Falla Infantil del ${item.year}`}
                        />
                      </button>
                      <div className="content">
                        <p className="heading">{item.year}</p>
                        <p>
                          <strong>{props.I18n.Artist}:</strong>{" "}
                          {item.Infantil.artista}
                          <br />
                          <strong>{props.I18n.Motto}:</strong>{" "}
                          {item.Infantil.lema}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Lightbox full-screen, perfecto para móvil */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={slides.map((s) => ({
          src: s.src,
          description: s.description,
        }))}
        plugins={[Captions]}
        render={{
          slide: ({ slide }) => (
            <figure style={{ margin: 0 }}>
              <img
                src={slide.src}
                alt={slide.description || ""}
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "100vh",
                  objectFit: "contain",
                }}
              />
              {slide.description && (
                <figcaption
                  style={{
                    textAlign: "center",
                    padding: "0.5rem 1rem",
                    fontSize: "0.9rem",
                  }}
                >
                  {slide.description}
                </figcaption>
              )}
            </figure>
          ),
        }}
      />
    </>
  );
};
